"use strict";
/**
 * Способности членства — «что эта роль может в этом магазине» (RBAC-2.1, AD-2.1-6).
 *
 * Чистые данные и одна чистая функция. Ни ввода-вывода, ни Express, ни знания о
 * маршрутах: шов проходит ровно здесь —
 *
 *   | Живёт в библиотеке              | Живёт в сервисе                          |
 *   | «что эта роль может»            | «чего требует этот маршрут»              |
 *   | роль → способность (этот файл)  | маршрут → способность (таблица сервиса)  |
 *
 * Маршруты у каждого сервиса свои; способности общие — потому что это и есть предмет
 * решения продакта «ассортимент и деньги».
 *
 * ⚠ AD-2.1-7 — способность членства ≠ право в `permissions`. Право в Core отвечает «что
 * этому человеку вообще позволено» (оси A и B), способность — «что эта роль может в этом
 * магазине». Вопросы разные, словари разные, общего пространства имён нет; способности
 * в таблицу `permissions` НЕ регистрируются.
 *
 * ⚠ AD-2.1-1 — лестницы ролей нет. `owner` проходит всюду не потому, что он «выше», а
 * потому что держит все способности по таблице ниже. Гейт спрашивает способность, а не
 * «роль не ниже такой-то»: порядок ролей — допущение, которое четвёртая роль через год
 * унаследовала бы молча.
 *
 * Правило одной строкой: **`staff` проводит то, что уже случилось; `manager` решает, что
 * будет продаваться и кому вернутся деньги.**
 */
Object.defineProperty(exports, "__esModule", { value: true });
exports.CAPABILITY_BY_ROLE = exports.MERCHANT_CAPABILITY_VALUES = exports.MERCHANT_CAPABILITIES = void 0;
exports.can = can;
const roles_1 = require("./roles");
/**
 * Канонический список — двенадцать, и больше их быть не должно: способность заводится
 * под черту, а не «на будущее».
 *
 * Трёх вещей здесь нет и не будет:
 *   • настроек магазина и состава сотрудников — они ВЛАДЕЛЬЧЕСКИЕ и закрыты своими
 *     гейтами (`requireMerchantOwner`, `MerchantMemberAccessPolicy`), а не набором
 *     способностей;
 *   • ответа на отзыв — он доступен любому члену, то есть совпадает с самим фактом
 *     членства. Способность, которую имеют все трое и которая ничего не разделяет, —
 *     это не способность, а шум.
 */
exports.MERCHANT_CAPABILITIES = {
    /** Провести заказ: confirm…deliver, заметки, ожидаемая дата. */
    ORDERS_PROCESS: 'orders:process',
    /** Отменить заказ. */
    ORDERS_CANCEL: 'orders:cancel',
    /** Вернуть деньги. */
    ORDERS_REFUND: 'orders:refund',
    /** Видеть товары — без этого нельзя принять заказ. */
    CATALOG_READ: 'catalog:read',
    /** Товары, коллекции, заявки в MDM: что будет продаваться. */
    CATALOG_WRITE: 'catalog:write',
    /** Синхронизация каталога. */
    CATALOG_SYNC: 'catalog:sync',
    /** Витрина: карусели, баннеры. */
    STOREFRONT_WRITE: 'storefront:write',
    /** Дашборды, отчёты, выгрузки. */
    ANALYTICS_READ: 'analytics:read',
    /** Смотреть, заводить, править покупателя; адреса, теги. */
    CUSTOMERS_WRITE: 'customers:write',
    /** Выгрузить базу, удалить покупателя — действие над магазином целиком. */
    CUSTOMERS_EXPORT: 'customers:export',
    /** Остатки: пересчёт правит один товар сегодня. */
    INVENTORY_ADJUST: 'inventory:adjust',
    /** Пороги: решают, при каком остатке товар исчезнет с витрины впредь. */
    INVENTORY_SETTINGS: 'inventory:settings',
};
exports.MERCHANT_CAPABILITY_VALUES = Object.values(exports.MERCHANT_CAPABILITIES);
/**
 * То, что `staff` делает за прилавком: происходящее с КОНКРЕТНЫМ покупателем и КОНКРЕТНЫМ
 * заказом. Всё, что действует на магазин целиком, — не здесь.
 */
const STAFF_CAPABILITIES = [
    exports.MERCHANT_CAPABILITIES.ORDERS_PROCESS,
    exports.MERCHANT_CAPABILITIES.CATALOG_READ,
    exports.MERCHANT_CAPABILITIES.CUSTOMERS_WRITE,
    exports.MERCHANT_CAPABILITIES.INVENTORY_ADJUST,
];
/**
 * `manager` держит все двенадцать. Разница с `owner` — не в способностях: владельческое
 * (лицо магазина, каналы, реквизиты, состав сотрудников) закрыто отдельными гейтами и
 * способностью не выражается (см. заголовок файла).
 */
const MANAGER_CAPABILITIES = exports.MERCHANT_CAPABILITY_VALUES;
exports.CAPABILITY_BY_ROLE = {
    [roles_1.MERCHANT_MEMBER_ROLES.OWNER]: exports.MERCHANT_CAPABILITY_VALUES,
    [roles_1.MERCHANT_MEMBER_ROLES.MANAGER]: MANAGER_CAPABILITIES,
    [roles_1.MERCHANT_MEMBER_ROLES.STAFF]: STAFF_CAPABILITIES,
};
/**
 * Может ли эта роль членства эту способность.
 *
 * `null` / `undefined` — «членства нет», и ответ `false`. Это НЕ ветка платформенного
 * админа: у него членства нет и не будет (RBAC-1.3 Р-6), и проходит он явной веткой в
 * каждом сервисе — выше этого вызова, а не сквозь него (AD-2.1-2).
 */
function can(role, capability) {
    if (!role)
        return false;
    return exports.CAPABILITY_BY_ROLE[role]?.includes(capability) ?? false;
}
